import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getYear, getMonth, getDate, parseISO } from 'date-fns';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import TextInput from '../TextInput';
import LockCopy from '../LockCopy';

import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './BirthdateForm.module.scss';

const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  'birthdate-form': birthdateFormClass,
  'date-inputs-container': dateInputsContainerClass,
  'month-input': monthInputClass,
  'day-input': dayInputClass,
  'year-input': yearInputClass
} = styles;

const BirthdateForm = ({ moveToNextStep }) => {
  const dispatch = useDispatch();

  const { authUser = {}, purchaseFlow = {} } = useSelector(({ authUser, webPurchase }) => ({
    authUser,
    purchaseFlow: webPurchase.purchaseFlow
  }));

  // assign most prioritized user data
  const { data: userData, webleadUpdated } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { _id: userId, dateOfBirth } = userData ?? {};

  // after submit, navigate to next step
  useEffect(() => {
    if (webleadUpdated) {
      dispatch(purchaseFlowOperations?.resetUpdateWeblead());
      dispatch(resetWebleadUpdated());
      moveToNextStep();
    }
  }, [dispatch, moveToNextStep, webleadUpdated]);

  const BirthdayFormSchema = Yup.object().shape({
    month: Yup.number()
      .min(1, 'Please enter a valid month')
      .max(12, 'Please enter a valid month')
      .required('Month is required!'),
    day: Yup.number()
      .min(1, 'Please enter a valid day')
      .max(31, 'Please enter a valid day')
      .required('Day is required!'),
    year: Yup.number()
      .min(1900, 'Please enter a valid year')
      .max(2020, 'Please enter a valid year')
      .required('Year is required!')
  });

  const handleSubmit = e => {
    const dateString = `${e?.year}/${e?.month}/${e?.day}`; // format like YYYY/MM/DD to be compatible with Safari, MM-DD-YYYY only compatible with Chrome + Firefox
    const isoDate = new Date(dateString)?.toISOString();
    const updatedUserBody = {
      dateOfBirth: isoDate,
      eFuneral: {
        lastScreen: {
          flow: 'everdays',
          screen: 'birthdateForm',
          date: new Date().toISOString()
        }
      }
    };

    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  const handleAutoFocusToDay = e => {
    if (e?.target?.value?.length === 2) {
      const month = Number(e.target.value);
      if (month >= 1 && month <= 12) {
        dayInput.current.focus();
      }
    }
  };

  const handleAutoFocusToYear = e => {
    if (e?.target?.value?.length === 2) {
      const day = Number(e.target.value);
      if (day >= 1 && day <= 31) {
        yearInput.current.focus();
      }
    }
  };

  const dayInput = useRef(null);
  const yearInput = useRef(null);

  const parsedDateOfBirth = parseISO(dateOfBirth);
  const year = getYear(parsedDateOfBirth) || '';
  const month = getMonth(parsedDateOfBirth) + 1 || ''; // month is zero-indexed, so add 1 to match standard month numbers
  const day = getDate(parsedDateOfBirth) || '';

  const initialValues = { month, day, year };

  return (
    <div className={birthdateFormClass}>
      <h3>When were you born?</h3>
      <p>We'll find you the right options tailored to your age.</p>
      <Formik
        initialValues={initialValues}
        validationSchema={BirthdayFormSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount
      >
        {({ dirty, isValid }) => (
          <Form>
            <div className={dateInputsContainerClass}>
              <TextInput
                autoFocus
                customClass={monthInputClass}
                type="number"
                name="month"
                placeholder="MM"
                onInput={e => handleAutoFocusToDay(e)}
              />
              <TextInput
                customClass={dayInputClass}
                type="number"
                name="day"
                placeholder="DD"
                ref={dayInput}
                onInput={handleAutoFocusToYear}
              />
              <TextInput
                customClass={yearInputClass}
                type="number"
                name="year"
                placeholder="YYYY"
                ref={yearInput}
              />
            </div>
            <LockCopy copy="Everdays will never sell your personalized data to third parties" />
            <button disabled={!isValid} type="submit">
              Continue
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BirthdateForm;
